import { DarkTheme, LightTheme } from '@mercedes-benz/mui5-theme';
import { ThemeProvider } from '@mui/material';
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useUserPreferences } from './UserPreferencesContext';

const ThemeContext = createContext(null);

const themeList = [
  {
    id: 0,
    name: 'light',
    theme: LightTheme,
  },
  {
    id: 1,
    name: 'dark',
    theme: DarkTheme,
  },
];

/**
 * Theme provider that derives its state from UserPreferencesContext.
 *
 * Must be rendered inside <UserPreferencesProvider>.
 * The `useMBUITheme()` API is unchanged so existing consumers
 * (e.g. ThemeSwitchToggle) keep working without modifications.
 */
const ThemeContextProvider = ({ children }) => {
  const { preferences, updatePreference } = useUserPreferences();

  // Map the persisted preference string ('light' | 'dark') → numeric id
  const activeThemeID = useMemo(() => {
    const match = themeList.find((t) => t.name === preferences.theme);
    return match ? match.id : 1; // default to dark
  }, [preferences.theme]);

  const activeTheme = useMemo(() => {
    const match = themeList.find((t) => t.id === activeThemeID);
    return match ? match.theme : DarkTheme;
  }, [activeThemeID]);

  // When any component calls setActiveThemeID(0|1), persist via preferences
  const setActiveThemeID = useCallback(
    (id) => {
      const match = themeList.find((t) => t.id === id);
      const themeName = match ? match.name : 'dark';
      updatePreference('theme', themeName);
    },
    [updatePreference]
  );

  const contextValues = { activeThemeID, setActiveThemeID };

  return (
    <ThemeContext.Provider value={contextValues}>
      <ThemeProvider theme={activeTheme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};

ThemeContextProvider.propTypes = {};

ThemeContextProvider.defaultProps = {};

const useMBUITheme = () => {
  const ctx = useContext(ThemeContext);
  if (ctx == null) {
    throw new Error('ThemeContext Provider missing');
  }

  return ctx;
};

export { ThemeContextProvider, useMBUITheme, themeList };
